import { all, put, takeEvery } from 'redux-saga/effects';
import { postman } from '../utils/postman';
import { createSelector } from 'reselect';
import { toast } from 'react-toastify';
import { errorMapping } from '../utils/errorMapping';
import {
    mockVehicles,
    mockTasksByVehicleId,
    mockSearchResults,
    mockCancelResult,
    mockAssignResult
} from '../mocks/rentedTasks.mock';

// * TYPES * //

const LOAD_VEHICLES_REQUEST = 'LOAD_VEHICLES_REQUEST';
const LOAD_VEHICLES_SUCCESS = 'LOAD_VEHICLES_SUCCESS';
const LOAD_VEHICLES_ERROR = 'LOAD_VEHICLES_ERROR';
const CLEAR_VEHICLES = 'CLEAR_VEHICLES';

const LOAD_TASKS_REQUEST = 'LOAD_TASKS_REQUEST';
const LOAD_TASKS_SUCCESS = 'LOAD_TASKS_SUCCESS';
const LOAD_TASKS_ERROR = 'LOAD_TASKS_ERROR';

const CANCEL_TASK_REQUEST = 'CANCEL_TASK_REQUEST';
const CANCEL_TASK_SUCCESS = 'CANCEL_TASK_SUCCESS';
const CANCEL_TASK_ERROR = 'CANCEL_TASK_ERROR';

const SEARCH_DIRECTIONS_REQUEST = 'SEARCH_DIRECTIONS_REQUEST';
const SEARCH_DIRECTIONS_SUCCESS = 'SEARCH_DIRECTIONS_SUCCESS';
const SEARCH_DIRECTIONS_ERROR = 'SEARCH_DIRECTIONS_ERROR';
const CLEAR_DIRECTIONS = 'CLEAR_DIRECTIONS';

const CLEAR_ERROR = 'CLEAR_ERROR';

const ASSIGN_TASK_REQUEST = 'ASSIGN_TASK_REQUEST'; 
const ASSIGN_TASK_SUCCESS = 'ASSIGN_TASK_SUCCESS';
const ASSIGN_TASK_ERROR = 'ASSIGN_TASK_ERROR';

const initial = {
    vehicles: [],
    tasks: [],
    directions: [],
    loading: false,
    error: null,
};

// * REDUCER * //

export default (state = initial, { type, payload }) => {
    switch (type) {
        case LOAD_VEHICLES_REQUEST:
        case LOAD_TASKS_REQUEST:
        case CANCEL_TASK_REQUEST:
        case SEARCH_DIRECTIONS_REQUEST:
        case ASSIGN_TASK_REQUEST:
            return { ...state, loading: true, error: null };

        case LOAD_VEHICLES_SUCCESS: {
            const updatedVehicles = Array.isArray(payload) ? payload : [payload];
            const updatedIds = updatedVehicles.map(v => v.id);

            return {
                ...state,
                loading: false,
                vehicles: [
                    ...state.vehicles.filter(v => !updatedIds.includes(v.id)),
                    ...updatedVehicles,
                ],
            };
        }

        case LOAD_TASKS_SUCCESS:
            return { ...state, loading: false, tasks: payload };

        case CANCEL_TASK_SUCCESS:
            return { ...state, cancelTaskResult: payload };

        case SEARCH_DIRECTIONS_SUCCESS:
            return { ...state, loading: false, directions: payload };

        case ASSIGN_TASK_SUCCESS:
            return { ...state, loading: false, assignedTask: payload };

        case LOAD_VEHICLES_ERROR:
        case LOAD_TASKS_ERROR:
        case CANCEL_TASK_ERROR:
        case SEARCH_DIRECTIONS_ERROR:
        case ASSIGN_TASK_ERROR:
            return { ...state, loading: false, error: payload };
        
        case CLEAR_DIRECTIONS:
            return { ...state, directions: [] };
        
        case CLEAR_VEHICLES:
            return { ...state, vehicles: [] };

        case CLEAR_ERROR:
            return { ...state, error: null };

        default:
            return state;
    }
};

//*  ACTION CREATORS  *//

export const loadVehicles = payload => {
    return {
        type: LOAD_VEHICLES_REQUEST,
        payload, 
    };
};

export const loadTasks = payload => {
    return {
        type: LOAD_TASKS_REQUEST,
        payload, 
    };
};

export const cancelTask = payload => {
    return {
        type: CANCEL_TASK_REQUEST,
        payload, 
    };
};

export const searchDirections = payload => {
    return {
        type: SEARCH_DIRECTIONS_REQUEST,
        payload, 
    };
};

export const assignTask = payload => {
    return {
        type: ASSIGN_TASK_REQUEST,
        payload, 
    };
};

export const clearDirections = () => {
    return {
        type: CLEAR_DIRECTIONS
    };
};

export const clearVehicles = () => {
    return {
        type: CLEAR_VEHICLES
    };
};

export const clearError = () => {
    return {
        type: CLEAR_ERROR
    };
}

// * SELECTORS * //

const stateSelector = state => state.rental;

export const vehiclesSelector = createSelector(stateSelector, s => s.vehicles);
export const tasksSelector = createSelector(stateSelector, s => s.tasks);
export const directionsSelector = createSelector(stateSelector, s => s.directions);
export const loadingSelector = createSelector(stateSelector, s => s.loading);
export const errorSelector = createSelector(stateSelector, s => errorMapping(s.error));

// * SAGA * //

function* loadVehiclesSaga({ payload }) {
    try {
        const { selectedIds } = payload;
        const result = yield postman.post('/rentalForm/loadVehicles', selectedIds);

        yield put({ type: LOAD_VEHICLES_SUCCESS, payload: result.data });
    } catch (error) {
        yield put({ type: LOAD_VEHICLES_ERROR, payload: error });
    }
}

function* loadTasksSaga({ payload }) {
    try {
        const { vehicleId } = payload;
        const result = yield postman.get(`/rentalForm/${vehicleId}/tasks`);

        yield put({ type: LOAD_TASKS_SUCCESS, payload: result.data });
    } catch (error) {
        yield put({ type: LOAD_TASKS_ERROR, payload: error });
    }
}

function* cancelTaskSaga({ payload }) {
    try {
        const { params, vehicleId } = payload;
        const result = yield postman.post(`/rentalForm/${vehicleId}/tasks/cancel`, params);

        if (result.isError) {
            if (result.message) {
                toast.error(result.message, { autoClose: false });
            }
        } else {
            if (result.message) {
                toast.info(result.message);
            }

            yield put(loadVehicles({ selectedIds: [vehicleId] }));
            yield put(loadTasks({ vehicleId }));

            yield put({ type: CLEAR_DIRECTIONS });
            yield put({ type: CANCEL_TASK_SUCCESS, payload: result });
        }
    } catch (error) {
        yield put({ type: CANCEL_TASK_ERROR, payload: error });
    }
}

function* searchDirectionsSaga({ payload }) {
    try {
        const { vehicleId, params } = payload;
        const result = yield postman.post(`/rentalForm/${vehicleId}/tasks/search`, params);

        yield put({ type: SEARCH_DIRECTIONS_SUCCESS, payload: result.data });
    } catch (error) {
        yield put({ type: SEARCH_DIRECTIONS_ERROR, payload: error });
    }
}

function* assignTaskSaga({ payload }) {
    try {
        const { vehicleId, params } = payload;
        const result = yield postman.post(`/rentalForm/${vehicleId}/tasks/assign`, params);

        if (result.isError) {
            if (result.message) {
                toast.error(result.message, { autoClose: false });
            }
        } else {
            if (result.message) {
                toast.info(result.message);
            }

            yield put(loadVehicles({ selectedIds: [vehicleId] }));
            yield put(loadTasks({ vehicleId }));

            yield put({ type: ASSIGN_TASK_SUCCESS, payload: result });
            yield put({ type: CLEAR_DIRECTIONS });
        }
    } catch (error) {
        yield put({ type: ASSIGN_TASK_ERROR, payload: error });
    }
}

export function* saga() {
    yield all([
        takeEvery(LOAD_VEHICLES_REQUEST, loadVehiclesSaga),
        takeEvery(LOAD_TASKS_REQUEST, loadTasksSaga),
        takeEvery(CANCEL_TASK_REQUEST, cancelTaskSaga),
        takeEvery(SEARCH_DIRECTIONS_REQUEST, searchDirectionsSaga),
        takeEvery(ASSIGN_TASK_REQUEST, assignTaskSaga),
    ]);
}
